import {Component, computed, inject} from '@angular/core';
import {Router} from '@angular/router';
import {TranslatePipe} from '@ngx-translate/core';
import {Menu} from 'primeng/menu';
import {InvoiceGrid} from '../invoice-grid/invoice-grid';
import {InvoiceStateId, InvoiceSummary as InvoiceSummaryModel, PaymentMethod} from '../../../../../api';
import {ElciaButton} from '../../../../components/buttons/button/button';
import {
  PeriodNavigatorComponent,
  PeriodRange,
  PeriodType
} from '../../../../components/period-navigator/period-navigator';
import {FinancesPeriodStore} from '../../services/finances-period.store';
import {InvoiceStoreService} from '../../services/invoice-store.service';
import {DirectDebitStore} from '../../services/direct-debit-store';

interface ExportMenuItem {
  value: 'preview' | 'export';
  label: string;
  icon: string;
}

@Component({
  selector: 'elcia-direct-debit-summary',
  imports: [InvoiceGrid, ElciaButton, Menu, PeriodNavigatorComponent, TranslatePipe],
  providers: [InvoiceStoreService],
  templateUrl: './direct-debit-summary.html',
  styleUrl: './direct-debit-summary.scss'
})
export class DirectDebitSummary {
  protected periodStore = inject(FinancesPeriodStore);
  protected store = inject(InvoiceStoreService);
  protected directDebitStore = inject(DirectDebitStore);
  private router = inject(Router);

  // Only invoices not yet exported are sent, to avoid a useless backend error.
  protected exportableInvoiceIds = computed(() =>
    this.store.invoices().filter(invoice => !invoice.directDebitExportedAt).map(invoice => invoice.id)
  );

  protected readonly isExporting = computed(() =>
    this.directDebitStore.previewMutation.isPending() || this.directDebitStore.exportMutation.isPending()
  );

  protected readonly menuItems: ExportMenuItem[] = [
    {value: 'preview', label: 'direct-debit.preview', icon: 'pi-file-excel'},
    {value: 'export', label: 'direct-debit.export', icon: 'pi-file-export'},
  ];

  constructor() {
    this.store.filterParams.set({
      searchTerm: null,
      customerIds: null,
      legalEntityIds: null,
      // Only issued invoices are collectable, so drafts are kept out of the screen entirely.
      stateNames: [InvoiceStateId.SENT],
      paymentMethods: [PaymentMethod.DirectDebit],
    });
  }

  protected setPeriod($event: PeriodRange) {
    this.periodStore.period.set($event);
  }

  protected navToEdit(invoice: InvoiceSummaryModel) {
    this.router.navigate(['invoice', 'form', invoice.id]);
  }

  protected navToCredit(invoice: InvoiceSummaryModel) {
    this.router.navigate(['credit-note', 'form', invoice.id]);
  }

  protected run(value: ExportMenuItem['value']) {
    const invoiceIds = this.exportableInvoiceIds();
    if (invoiceIds.length === 0) return;
    if (value === 'preview') {
      this.directDebitStore.previewMutation.mutate(invoiceIds);
    } else {
      this.directDebitStore.exportMutation.mutate(invoiceIds);
    }
  }

  protected readonly PeriodType = PeriodType;
}
